<script>
import axios from 'axios';
import dayjs from 'dayjs';
import { mapState } from 'vuex';
import { addNotification, clearNotification } from '@/utils/notification';
import { products, locker } from '@/constants/launchpad';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Button from '@/components/Button';
import ClaimDate from '@/components/Launchpad/ClaimDate';

dayjs.extend(customParseFormat);

export default {
  name: 'Claim',
  components: {
    Button,
    ClaimDate,
  },
  data() {
    return {
      total: '0',
      locked: '0',
      unblocked: '0',
      claimed: '0',
      loading: false,
      processing: false,
    };
  },
  computed: {
    ...mapState({
      ethAddress: (state) => state.User.ethAddress,
      chainId: (state) => state.User.chainId,
    }),
    saleId() {
      return locker[this.lockerId].saleId;
    },
    saleChainId() {
      return locker[this.lockerId].saleChainId;
    },
    lockerId() {
      return this.$route.params.pid;
    },
    apiPath() {
      return String(this.$route.params.pid) === '1' ? 'vesting' : 'vesting-2';
    },
    lockerChain() {
      return this.lockerParams.lockerChain || '';
    },
    schedule() {
      return locker[this.lockerId].unlockSchedule || [];
    },
    lockerParams() {
      return locker[this.lockerId] || {};
    },
    productParams() {
      return (
        products[this.saleId] || {}
      );
    },
    isAllowedChain() {
      return [1, 4, 56, 97, 43113, 43114].includes(this.chainId);
    },
  },
  async mounted() {
    this.getBalance();
  },
  methods: {
    isPassed(d) {
      return dayjs().isAfter(dayjs(d, 'DD.MM.YYYY'));
    },
    getIconSrc() {
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(`@/assets/img/${this.$chain.getName()}/launchpad/${
        this.productParams.icon
      }.jpg`);
    },
    async claim() {
      this.loading = true;
      try {
        const amount = this.unblocked;
        const amountWei = this.$contracts.toWei(amount).toString();
        const address = this.ethAddress;
        const saleId = this.lockerId;
        const chainId = this.lockerChain;
        const res = await axios.post(`/${this.apiPath}/claim/${address}/`, {
          saleId,
          chainId,
        });
        if (res.status !== 200) {
          console.log('no wallet');
          return false;
        }
        const message = res.data.data?.message;
        const signature = res.data.data?.signature;
        const lastBlock = res.data.data?.lastUpdateBlock;
        const currentBlock = res.data.data?.currentBlock;
        const token = res.data.data?.rewardToken;

        this.$contracts
          .getContract('launchpadVesting')
          .methods.harvest(amountWei, lastBlock, currentBlock, message, signature, token)
          .send({ from: address })
          .once('transactionHash', (hash) => {
            addNotification(
              {
                type: 'info',
                title: 'Pending claim:',
                data: { hash },
              },
              { root: true },
            );
          })
          .once('confirmation', () => {
            clearNotification('info');
            addNotification(
              {
                type: 'success',
                title: 'Successful claim!',
                config: { duration: 15000 },
              },
              { root: true },
            );
            this.unblocked = '0';
            this.loading = false;
          })
          .once('error', () => {
            clearNotification('info');
            addNotification(
              {
                type: 'warn',
                title: 'Claim failed',
                config: { duration: 5000 },
              },
              { root: true },
            );
            this.loading = false;
          });
        this.processing = true;
        return true;
      } catch (e) {
        console.log(e);
        this.loading = false;
        return false;
      }
    },
    async getBalance() {
      this.loading = true;
      try {
        const saleId = this.lockerId;
        const chainId = this.lockerChain;
        const res = await axios.get(`/${this.apiPath}/balance/${this.ethAddress}/`, {
          params: {
            saleId,
            chainId,
          },
        });
        if (res.status !== 200) {
          console.log('no wallet');
          return false;
        }
        const lastBlockShadow = res.data.data?.lastUpdateBlock;
        const token = res.data.data?.rewardToken;
        this.total = res.data.data?.total ?? '0';
        this.locked = res.data.data?.locked ?? '0';
        this.unblocked = res.data.data?.unlocked ?? '0';
        this.claimed = res.data.data?.claimed ?? '0';
        const lastBlockChain = await this.$contracts
          .getContract('launchpadVesting')
          .methods.getLastBlock(this.ethAddress, token)
          .call();
        this.loading = false;
        this.processing = Number(lastBlockChain) !== Number(lastBlockShadow);
        return true;
      } catch (e) {
        this.loading = false;
        console.error(e);
        return false;
      }
    },
  },
};
</script>

<template>
  <div class="claim">
    <div v-if="!isAllowedChain">
      <v-row justify="center">
        <h1>{{ $t('upcoming.ethereum-only') }}</h1>
      </v-row>
    </div>
    <div v-else-if="Object.keys(lockerParams).length === 0">
      <v-row justify="center">
        <h1>{{ $t('upcoming.no-products') }}</h1>
      </v-row>
    </div>
    <div v-else-if="chainId !== 1">
      <v-row justify="center">
        <h1>Please, switch to {{ lockerChain }}</h1>
      </v-row>
    </div>
    <div class="mt-10 mb-10" v-else>
      <div class="main-block upcoming-card mb-10" v-if="!Number(total)">
        <div class="text-left mb-5">
          <h4 class="claim__subline">{{ $t('vesting.not-registered') }}</h4>
        </div>
      </div>
      <div class="main-block upcoming-card mb-10" v-else>
        <div class="text-left mb-5">
          <h4 class="claim__subline">{{ $t('vesting.congrats') }}</h4>
        </div>
        <p class="claim__p">{{ $t('vesting.congrats-text') }}</p>
      </div>
      <v-row class="align-stretch">
        <v-col sm="8" cols="12">
          <p class="label mb-7">{{ $t('vesting.summary') }}</p>
          <div class="upcoming-card main-block">
            <v-row>
              <v-col sm="4" cols="12">
                <v-row class="upcoming-info">
                  <v-col sm="6" xs="12">
                    {{ $t('vesting.total') }}
                  </v-col>
                  <v-col sm="6" xs="12">
                    {{ $BN(total) | amount(5) }}
                  </v-col>
                </v-row>
                <v-row class="upcoming-info">
                  <v-col sm="6" xs="12">
                    {{ $t('vesting.locked') }}
                  </v-col>
                  <v-col sm="6" xs="12">
                    {{ $BN(locked) | amount(5) }}
                  </v-col>
                </v-row>
                <v-row class="upcoming-info">
                  <v-col cols="4">
                    {{ $t('vesting.claimed') }}
                  </v-col>
                  <v-col>
                    {{ $BN(claimed) | amount(5) }}
                  </v-col>
                </v-row>
              </v-col>
              <v-col sm="8" cols="12" class="d-flex justify-space-around">
                <div class="d-flex justify-center align-center flex-column flex-sm-row">
                  <div class="text-center mr-3 d-flex flex-column align-center">
                    <div class="claim-available" v-if="processing">
                      {{ $t('vesting.processing') }}
                    </div>
                    <div class="claim-available" v-else>
                      {{ $t('vesting.available') }}
                    </div>
                    <p class="unblocked">{{ $BN(unblocked) | amount(5) }}</p>
                  </div>
                  <div>
                    <Button
                      @click="claim"
                      :disabled="loading || !$BN(unblocked).gt(0) || processing"
                      >{{ $BN(unblocked).gt(0) ? 'Claim' : 'Nothing to claim' }}</Button
                    >
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-col>
        <v-col sm="4" cols="12" class="d-flex flex-column">
          <p class="label mb-7">{{ $t('vesting.period') }}</p>
          <div class="upcoming-card main-block h100">
            <v-row class="upcoming-info">
              <v-col sm="6" xs="12">
                {{ $t('vesting.start') }}
              </v-col>
              <v-col sm="6" xs="12">
                {{ lockerParams.startDate }}
              </v-col>
            </v-row>
            <v-row class="upcoming-info">
              <v-col sm="6" xs="12">
                {{ $t('vesting.end') }}
              </v-col>
              <v-col sm="6" xs="12">
                {{ lockerParams.endDate }}
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <div class="text-left mt-3">
        <div v-html="$t('vesting.notify')" class="claim-available"></div>
      </div>
      <div class="text-left mb-5 mt-10">
        <h4 class="claim__subline">{{ $t('vesting.schedule') }}</h4>
      </div>
      <v-row class="mb-5 schedule-info">
        <v-col sm="6" cols="12">
          <p>{{ $t('vesting.schedule-text') }}</p>
        </v-col>
        <v-col sm="6" cols="12">
          <p>{{ $t('vesting.schedule-desc') }}</p>
          <p class="font-weight-bold">{{ productParams.vestingInfo }}</p>
          <p>{{ $t('vesting.token-price', { price: productParams.price }) }}</p>
        </v-col>
      </v-row>
      <div class="main-block upcoming-card mb-10">
        <div class="text-center">
          <div class="claim__icon">
            <img :src="getIconSrc()" />
          </div>
          <h1>{{ productParams.tokenTicker }} unlock</h1>
          <ClaimDate
            :percent="schedule[0].percent"
            :label="schedule[0].label"
            :date="schedule[0].date"
            :passed="schedule[0].passed"
          />
        </div>
        <div class="schedule__grid">
          <div v-for="(item, index) in schedule.slice(1)" :key="index" class="schedule__item">
            <ClaimDate
              :percent="item.precent"
              :label="`Month ${index + 1}`"
              :date="item.date"
              :passed="isPassed(item.date)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="text-right claim__link">
      <router-link :to="{ name: 'agreement' }">User Agreement</router-link>
    </div>
  </div>
</template>

<style lang="scss">
.claim {
  width: 95%;
  margin: 0 auto;

  &__link {
    font-size: 1.8rem;
    margin-top: 5rem;
  }

  &__icon img {
    width: 10.5rem;
    height: 10.5rem;
    border-radius: 50%;
  }

  p {
    font-size: 2.2rem;
  }

  &__p {
    font-size: 1.6rem;
    color: $grey-font-color;

    body.binance & {
      color: $b-grey-font-color;
    }
  }

  .upcoming-card {
    height: auto;
    padding: 2.8rem 3.2rem 2.9rem 3.2rem;

    .btn {
      padding: 2rem 5rem;
    }

    &.h100 {
      height: 100%;
    }

    .col:first-child {
      text-transform: none;
    }
  }

  .upcoming-info {
    .col {
      font-size: 1.6rem;
      font-weight: 600;

      &:first-child {
        color: $grey-font-color;

        body.binance & {
          color: $b-grey-font-color;
        }
      }

      &:last-child {
        text-align: right;
      }
    }
  }

  &__title {
    font-weight: 900;
    font-size: 4.4rem;
  }

  &__subline {
    font-weight: 700;
    font-size: 3.4rem;
  }

  .label,
  .unblocked {
    font-size: 2.4em;
    font-weight: 600;
    margin-bottom: 0;
  }

  &-available {
    color: $grey-font-color;

    body.binance & {
      color: $b-grey-font-color;
    }
  }

  .schedule {
    &-info {
      color: #7478a0;
    }

    &__grid {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
    }

    &__item {
      text-align: center;
      width: 20rem;
    }
  }
}
</style>
